import createEngine, {
  DagreEngine,
  DefaultLinkModel,
  DiagramEngine,
  DiagramModel,
  PathFindingLinkFactory
} from "@projectstorm/react-diagrams";
import { LoggerFactory } from "lego-gui-lib";
import { runInAction } from "mobx";
import { ReactText } from "react";
import { ConnectionDef } from "./connection/schema";
import { CustomDeleteItemsAction } from "./CustomDeleteItemsAction";
import {
  AdvancedLinkFactory,
  AdvancedLinkModel,
  AdvancedPortModel
} from "./treeBlock/components/AdvancedLinkModel";
import {
  DefaultTreeBlockFactory,
  DefaultTreeBlockModel
} from "./treeBlock/components/DefaultTreeBlockModel";
import { RootBlockFactory } from "./treeBlock/components/RootBlockFactory";
import {
  BlockObject,
  OutputType
} from "./treeBlock/treeBlockOutputsDataSource";
import { LocalOutput, treeBlockConfigurations } from "./treeBlock/schema";
import { showErrorMessage } from "./treeBlock/schema/validators";
import { TreeBlockType } from "./treeBlock/TreeBlockType";
import { isNil } from "lodash";

export interface TreeEngine {
  engine: DiagramEngine;
  selectNode: (nodeId: string) => void;
  createOrUpdateRootBlock: () => void;
  autoDistribute: () => void;
  updateBlockMeta: (blockObject: BlockObject) => void;
  clearConnections: () => void;
  addBlockOutput: (blockId: ReactText, output: LocalOutput) => void;
  removeBlockOutput: (blockId: ReactText, output: LocalOutput) => boolean;
  addBlock: (node: BlockObject, position: any) => void;
  updateConnection: ($event: any) => void;
  initBlocks: (blocks: BlockObject[]) => void;
  initConnections: (connections: ConnectionDef[]) => void;
  registerListeners: (onDelete: any, onChangeSelection: any) => void;
  removeBlock: (node: any) => void;
  fetchTreeStructureChanges: () => void;
  lockTree: () => void;
}

export const CreateTreeEngine = (
  treeBlocks: any,
  connections: any,
  outputs: any
) => {
  const log = LoggerFactory.create("TreeEngine");
  const treeDataSource = treeBlocks;
  const connectionDataSource = connections;
  const outputDataSource = outputs;

  // callback functions
  let onDeleteBlock: any;
  let onChangeBlockSelection: any;

  // create an instance of the engine with delete overrides
  const engine = createEngine({ registerDefaultDeleteItemsAction: false });
  engine.getNodeFactories().registerFactory(new DefaultTreeBlockFactory());
  engine.getNodeFactories().registerFactory(new RootBlockFactory());

  //2) setup the diagram model
  const model = new DiagramModel();

  // install the model into the engine
  engine.setModel(model);
  engine.getLinkFactories().registerFactory(new AdvancedLinkFactory());

  // override default delete node key mapping
  engine.getActionEventBus().registerAction(
    new CustomDeleteItemsAction({
      keyCodes: [46, 8],
      modifiers: {
        ctrlKey: true
      }
    })
  );

  const autoDistributeEngine: DagreEngine = new DagreEngine({
    graph: {
      rankdir: "LR",
      ranker: "network-simplex",
      marginx: 48,
      marginy: 48,
      nodesep: 80,
      edgesep: 40
    },
    includeLinks: true
  });

  const autoDistribute = (): void => {
    autoDistributeEngine.redistribute(model);
    reroute();
    engine.zoomToFit();
    engine.repaintCanvas();
  };

  const reroute = (): void => {
    engine
      .getLinkFactories()
      .getFactory<PathFindingLinkFactory>(PathFindingLinkFactory.NAME)
      .calculateRoutingMatrix();
  };

  const addNodeToModel = (
    node: DefaultTreeBlockModel,
    nodeRemoved: any
  ): void => {
    node.registerListener({
      /* prevent node multi select */
      eventWillFire: (event: any & { function: string }) => {
        if (event.function === "selectionChanged") {
          if (model.getSelectedEntities().length > 1) {
            model.clearSelection();
          }
        }
      },
      selectionChanged: onChangeBlockSelection,
      entityRemoved: nodeRemoved
    });
    model.addNode(node);
  };

  const getNode = (nodeId: string): DefaultTreeBlockModel =>
    model.getNode(nodeId) as DefaultTreeBlockModel;

  const selectNode = (nodeId: string): void =>
    getNode(nodeId).setSelected(true);

  const getConfig = (block: BlockObject) => {
    return typeof treeBlockConfigurations[block.type].configMapper ===
      "function"
      ? // @ts-ignore
        treeBlockConfigurations[block.type].configMapper(block)
      : "";
  };

  const generateModel = (node: BlockObject): DefaultTreeBlockModel => {
    return engine
      .getNodeFactories()
      .getFactory("default-tree-block")
      .generateModel({
        initialConfig: {
          name: node.type,
          id: node.id,
          config: getConfig(node),
          meta: node.meta
        }
      }) as DefaultTreeBlockModel;
  };

  const initBlocks = (blocks: BlockObject[]) => {
    log.debug("initBlocks", blocks);
    if (blocks) {
      blocks.forEach((blockObject: BlockObject) => {
        const newBlock = prepareBlockModel(blockObject);
        log.debug("initBlocks newBlock", newBlock);
        addNodeToModel(newBlock, onDeleteBlock);
        if (blockObject.meta.info.includes(TreeBlockType.ROOT)) {
          initVirtualRootBlockConnection(blockObject, newBlock);
        }
      });
    }
  };

  const prepareBlockModel = (
    blockObject: BlockObject,
    position?: any
  ): DefaultTreeBlockModel => {
    updateBlockMeta(blockObject);
    const newBlock: DefaultTreeBlockModel = generateModel(blockObject);
    newBlock.addPort(createInPort());
    outputDataSource
      .initConfiguredOutputs(blockObject.id)
      .forEach((output: LocalOutput) => {
        newBlock.addOutput(output);
      });
    outputDataSource.initOutputOptions(blockObject.id, blockObject.type);
    /* If position is determined it means that newly created block is being prepared */
    if (position) {
      newBlock.setPosition(position);
    } else {
      /* newBlock.getID() is the same id for newBlock and prevBlock */
      setPreviousBlockPosition(newBlock.getID(), newBlock);
    }
    newBlock.updateDimensions({ width: 200, height: 120 });
    log.debug("newBlock outputs", blockObject.type, newBlock.getOutPorts());
    return newBlock;
  };

  const initVirtualRootBlockConnection = (
    blockObject: BlockObject,
    newBlock: DefaultTreeBlockModel
  ) => {
    log.debug("initVirtualRootBlockConnection", blockObject);
    const rootBlock = getNode(TreeBlockType.ROOT);
    // @ts-ignore
    const link = rootBlock
      .getPort("OUT")
      // @ts-ignore
      .link<DefaultLinkModel>(
        newBlock.getPort("IN") || newBlock.addInPort("IN")
      );
    link.registerListener({
      /* prevent link multi select */
      eventWillFire: preventLinkMultiSelect,
      selectionChanged: connectionSelected,
      entityRemoved: rootLinkRemoved
    });
    model.addLink(link);
  };

  const initConnections = (connections: ConnectionDef[]) => {
    if (connections) {
      connections.forEach((connection: ConnectionDef) => {
        const parentNode: DefaultTreeBlockModel = getNode(
          connection.from.blockRef.toString()
        );
        const childNode: DefaultTreeBlockModel = getNode(
          connection.to.blockRef.toString()
        );
        if (!parentNode || !childNode) {
          return;
        }
        const outPort = prepareOutPortForConnection(connection, parentNode);
        const link: AdvancedLinkModel = outPort.link<AdvancedLinkModel>(
          childNode.getPort("IN") || childNode.addInPort("IN")
        );
        // @ts-ignore
        link.setId(connection.id.toString());
        link.registerListener({
          /* prevent link multi select */
          eventWillFire: preventLinkMultiSelect,
          selectionChanged: connectionSelected,
          entityRemoved: linkRemoved
        });
        model.addLink(link);
      });
    }
  };

  const prepareOutPortForConnection = (
    connection: ConnectionDef,
    parentNode: DefaultTreeBlockModel
  ): AdvancedPortModel => {
    const optionId = outputDataSource.prepareOutputOptionId(
      // @ts-ignore
      parentNode.options.name as TreeBlockType,
      connection.id,
      connection.from.output
    );
    let portOut = parentNode.getOutPortByOptionId(
      optionId
    ) as AdvancedPortModel;
    if (!portOut) {
      //@ts-ignore
      portOut = parentNode.addOutPort(
        `${connection.from.output}`,
        `${connection.from.output}`,
        connection.type,
        optionId
      ) as AdvancedPortModel;
    }
    portOut.setOutputType(connection.type);
    return portOut;
  };

  const lockTree = () => {
    model.setLocked(true);
  };

  const rootLinkRemoved = async ($event: any) => {
    $event.stopPropagation();
    showErrorMessage("hns:tree:treeBlock:cantRemoveRootConnection");
    // fetch prevents root block from loosing connection to in and out ports
    await fetchTreeStructureChanges();
  };

  const rootBlockRemoved = ($event: any) => {
    $event.stopPropagation();
    showErrorMessage("hns:tree:treeBlock:cantRemoveRootBlock");
  };

  const createOrUpdateRootBlock = (): void => {
    const virtualRoot = engine
      .getNodeFactories()
      .getFactory("root-block")
      .generateModel({}) as DefaultTreeBlockModel;
    const rootPort: AdvancedPortModel = new AdvancedPortModel(
      false,
      "OUT",
      "OUT",
      OutputType.SIMPLE,
      TreeBlockType.ROOT
    );
    virtualRoot.updateDimensions({ width: 64, height: 77 });
    setPreviousBlockPosition(TreeBlockType.ROOT, virtualRoot);
    virtualRoot.addPort(rootPort);
    addNodeToModel(virtualRoot, rootBlockRemoved);
  };

  const updateBlockMeta = (blockObject: BlockObject) => {
    const newBlock: DefaultTreeBlockModel = getNode(blockObject.id);
    if (newBlock) {
      newBlock.updateMeta(blockObject.meta);
      newBlock.updateConfig(getConfig(blockObject));
    }
  };

  const setPreviousBlockPosition = (
    blockId: string,
    blockToUpdate: DefaultTreeBlockModel
  ) => {
    const prevNode = model.getNode(blockId);
    if (prevNode) {
      blockToUpdate.setPosition(prevNode.getPosition());
    }
  };

  const createInPort = (): AdvancedPortModel =>
    new AdvancedPortModel(true, "IN", "IN", OutputType.SIMPLE, "IN");

  const removeOutputAfterLinkRemoved = ($event: any) => {
    log.debug("removeOutputAfterLinkRemoved", $event);
    const nodeModel: DefaultTreeBlockModel = getNode(
      $event.entity.sourcePort?.parent.options.id
    );
    log.debug("removeOutputAfterLinkRemoved nodeModel", nodeModel);
    const outputToRemove = nodeModel?.getOutPort(
      $event.entity.sourcePort.options.label
    );
    log.debug("removeOutputAfterLinkRemoved output", outputToRemove);

    if (nodeModel && outputToRemove) {
      // @ts-ignore
      treeBlockConfigurations[nodeModel.name].outputOptions?.forEach(
        (output: LocalOutput) => {
          // @ts-ignore
          if (!output.isAddedByDefault && output.type === outputToRemove.type) {
            nodeModel.removePort(outputToRemove as AdvancedPortModel);
          }
        }
      );
    }
  };

  const clearConnections = () =>
    model.getLinks().forEach(link => model.removeLink(link));

  const addBlock = (blockObject: BlockObject, position: any) => {
    runInAction(() => {
      treeDataSource.treeStructure.blocks.push(blockObject);
    });
    const newBlock: DefaultTreeBlockModel = prepareBlockModel(
      blockObject,
      position
    );
    addNodeToModel(newBlock, onDeleteBlock);
    engine.repaintCanvas();
  };

  const removeBlock = async ($event: any) => {
    if (validateBlock($event)) {
      try {
        treeDataSource.oneDataSource.change({
          ...findBlockById($event.entity.options.id)
        });
        await treeDataSource.oneDataSource.delete();
        await fetchTreeStructureChanges();
      } catch (e) {
        showErrorMessage("hns:tree:treeBlock:removeBlockFailed");
      }
    }
  };

  const findBlockById = (id: number) => {
    return treeDataSource.treeStructure.blocks.find(
      (block: any) => block.id === id
    );
  };

  const addBlockOutput = (blockId: ReactText, output: LocalOutput): void => {
    const nodeModel: DefaultTreeBlockModel = getNode(blockId.toString());
    if (
      nodeModel
        .getOutPorts()
        .findIndex(port => port.getName() === output.value) < 0
    ) {
      nodeModel.addOutput(output);
    }
    log.debug("addBlockOutput outputs", nodeModel.getOutPorts(), output);
    engine.repaintCanvas();
  };

  const removeBlockOutput = (
    blockId: ReactText,
    output: LocalOutput
  ): boolean => {
    const nodeModel: DefaultTreeBlockModel = getNode(blockId.toString());
    const outputToRemove = nodeModel.getOutPortByOptionId(output.optionId);
    if (outputToRemove && Object.keys(outputToRemove.getLinks()).length > 0) {
      showErrorMessage("hns:tree:treeBlock:outputUsed");
      return false;
    } else {
      nodeModel.removePort(outputToRemove as AdvancedPortModel);
      engine.repaintCanvas();
      return true;
    }
  };

  const fetchTreeStructureChanges = async () =>
    await treeDataSource.getTreeStructure();

  const connectionSelected = ($event: any) =>
    connectionDataSource.oneDataSource.change($event.entity.options);

  const addConnection = async (newConnection: ConnectionDef) =>
    connectionDataSource.saveConnection(newConnection);

  const linkUpdated = async ($event: any) => {
    log.debug("linkUpdated", $event);
    clearUnconnectedLinks($event);
    if ($event.entity.sourcePort.parent.options.name === TreeBlockType.ROOT) {
      await updateRootConnection($event);
      engine.repaintCanvas();
    } else {
      await updateConnection($event);
      engine.repaintCanvas();
    }
  };

  const updateConnection = async ($event: any) => {
    const treeBlock = $event.entity.sourcePort.parent.options;
    const outputValue = $event.entity.sourcePort.options.name;
    let finalOutput = outputValue;
    if (treeBlock.name === TreeBlockType.TRAFFIC_DISTRIBUTION) {
      /* remove UUID from output value before sending */
      finalOutput = finalOutput.split("_")[0];
    }
    const newConnection = {
      from: {
        blockRef: $event.entity.sourcePort.parent.options.id,
        output: finalOutput
      },
      to: {
        blockRef: $event.entity.targetPort.parent.options.id
      },
      type: $event.entity.sourcePort.outputType
    };
    if (validateConnection($event)) {
      try {
        const resp = await addConnection(newConnection);
        if (!isNil(resp.id)) {
          runInAction(() => {
            outputDataSource.updateOutputOption(
              treeBlock.name,
              treeBlock.id,
              outputValue,
              resp.id
            );
          });
          updateGraphOutputOptionId($event, resp.id);
          log.debug("outputOptions", outputs.outputOptions);
          await fetchTreeStructureChanges();
        } else {
          model.removeLink($event.entity);
          removeOutputAfterLinkRemoved($event);
          await fetchTreeStructureChanges();
          log.debug("model after removeLink", model);
        }
      } catch (e) {
        showErrorMessage("hns:tree:treeBlock:linkUpdateFailed");
        log.error("failed connection", $event.entity.options.id, e);
        model.removeLink($event.entity);
      }
    }
  };

  /* When full connection is created, all incomplete ones are cleared */
  const clearUnconnectedLinks = ($event: any) => {
    if (
      !$event.entity.sourcePort?.parent ||
      !$event.entity.targetPort?.parent
    ) {
      model.removeLink($event.entity);
      engine.repaintCanvas();
    }
  };

  const linkRemoved = async ($event: any) => {
    try {
      await connectionDataSource.deleteConnection($event.entity.options.id);
      await fetchTreeStructureChanges();
      engine.repaintCanvas();
      removeOutputAfterLinkRemoved($event);
    } catch (e) {
      showErrorMessage("hns:tree:treeBlock:linkRemoveFailed");
    }
  };

  const updateRootConnection = async ($event: any) => {
    const targetBlockInputConnections = model
      .getLinks()
      .filter(
        (connection: any) =>
          connection.targetPort?.parent.options.id ===
          $event.entity.targetPort?.parent.options.id
      );
    if (targetBlockInputConnections.length >= 2) {
      showErrorMessage("hns:tree:treeBlock:inputAlreadyTakenCannotConnectRoot");
      model.removeLink($event.entity);
    } else {
      try {
        await treeDataSource.oneDataSource.changeTreeRootBlock(
          $event.entity.targetPort.parent.options.id
        );
        await fetchTreeStructureChanges();
      } catch (e) {
        log.error(e);
        showErrorMessage("hns:tree:treeBlock:changeRootFailed");
      }
    }
  };

  const validateBlock = ($event: {
    entity: DefaultTreeBlockModel;
    firing: boolean;
    stopPropagation: () => any;
  }): boolean => {
    let valid = true;

    const rootLink = model
      .getLinks()
      // @ts-ignore
      .find(link => link?.sourcePort?.optionId === "ROOT");
    log.debug("validateBlock $event", $event);
    log.debug("validateBlock rootLink", rootLink);

    const isConnectedToRoot =
      // @ts-ignore
      rootLink?.targetPort.options.id === $event.entity.portsIn[0].options.id;
    log.debug("validateBlock isConnectedToRoot", isConnectedToRoot);
    if (isConnectedToRoot) {
      $event.stopPropagation();
      showErrorMessage("hns:tree:treeBlock:cantRemoveBlockItsConnected");
      valid = false;
    }

    return valid;
  };

  const validateConnection = ($event: any): boolean => {
    log.debug("validateConnection $event.entity", $event.entity);
    let valid = true;
    if ($event.entity.targetPort.parent.options.name === TreeBlockType.ROOT) {
      showErrorMessage("hns:tree:treeBlock:cantAddInputToRoot");
      model.removeLink($event.entity);
      valid = false;
    }
    if ($event.entity.sourcePort.options.name === "IN") {
      showErrorMessage(
        "hns:tree:treeBlock:cantCreateConnectionStartingFromInput"
      );
      model.removeLink($event.entity);
      valid = false;
    }
    if (
      treeDataSource.treeStructure.connections.findIndex(
        (connection: ConnectionDef) =>
          connection.id === $event.entity.sourcePort.optionId ||
          /* Check for default outputs */
          (connection.from.output === $event.entity.sourcePort.optionId &&
            connection.from.blockRef ===
              $event.entity.sourcePort.parent.options.id)
      ) >= 0
    ) {
      showErrorMessage("hns:tree:treeBlock:outputUsed");
      model.removeLink($event.entity);
      valid = false;
    }
    return valid;
  };

  const updateGraphOutputOptionId = ($event: any, newId: string) => {
    /* Prevent update if newId is undefined */
    if (!isNil(newId)) {
      /* Find graph node */
      const parentNode: DefaultTreeBlockModel = getNode(
        $event.entity.sourcePort.parent.getID()
      );
      /* Select proper output */
      const portOut = parentNode.getOutPortByOptionId(
        $event.entity.sourcePort.getOptionId()
      ) as AdvancedPortModel;

      const optionId = outputDataSource.prepareOutputOptionId(
        parentNode.name,
        newId,
        portOut.getOptions().name
      );
      /* Update optionId */
      portOut.setOptionId(optionId);
    }
  };

  const registerListeners = (onDelete: any, onChangeSelection: any) => {
    /* Connections related */
    model.registerListener({
      linksUpdated: (event: any) =>
        event.link.registerListener({
          sourcePortChanged: linkUpdated,
          targetPortChanged: linkUpdated
        })
    });
    /* Blocks related */
    onDeleteBlock = onDelete;
    onChangeBlockSelection = onChangeSelection;
  };

  const preventLinkMultiSelect = (event: any & { function: string }) => {
    if (event.function === "selectionChanged") {
      if (model.getSelectedEntities().length > 1) {
        model.clearSelection();
        model.getLink(event.entity.getOptions().id).setSelected(true);
      }
    }
  };

  return {
    engine,
    lockTree,
    selectNode,
    createOrUpdateRootBlock,
    autoDistribute,
    updateBlockMeta,
    clearConnections,
    addBlockOutput,
    removeBlockOutput,
    addBlock,
    updateConnection,
    initBlocks,
    initConnections,
    registerListeners,
    removeBlock,
    fetchTreeStructureChanges
  };
};
